import { Link, Outside, Tooltip, UIHeading4, UIText2 } from '@cian/ui-kit';
import { IconFunctionalInformation16 } from '@cian/ui-kit-design-tokens/icons';
import React, { useMemo, useCallback, useState } from 'react';

import * as styles from './MortgagePaymentInformer.css';
import { MortgagePaymentTooltipContent } from './MortgagePaymentTooltipContent';
import { prepareTitle } from './utils/prepareTitle';

interface IMortgagePaymentInformerProps {
  title: string;
  programTypeWithRate: string;
  tooltipTitle: string;
  tooltipLink: string;
  onPriceClick: () => void;
  onTooltipShow: () => void;
  onTooltipLinkClick: () => void;
}

export const MortgagePaymentInformer: React.FC<IMortgagePaymentInformerProps> = ({
  title,
  programTypeWithRate,
  tooltipTitle,
  tooltipLink,
  onPriceClick,
  onTooltipShow,
  onTooltipLinkClick,
}) => {
  const { mainPart, highlightedPart } = useMemo(() => prepareTitle(title), [title]);

  const [open, setOpen] = useState(false);
  const onClose = useCallback(() => setOpen(false), []);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpen(!open);

      /* istanbul ignore else */
      if (!open) {
        onTooltipShow();
      }
    },
    [onTooltipShow, open],
  );

  return (
    <div className={styles['container']} data-testid="mortgage-payment-informer">
      <div className={styles['header']}>
        <div>
          <UIHeading4>
            {mainPart}
            {highlightedPart && (
              <>
                {mainPart && ' '}
                <Link
                  href={tooltipLink}
                  data-testid="mortgage-payment-price-link"
                  onClick={onPriceClick}
                  fontWeight="bold"
                  target="_blank"
                >
                  {highlightedPart}
                </Link>
              </>
            )}
          </UIHeading4>
        </div>
        <Outside onOutside={onClose}>
          <div className={styles['icon']} onClick={onClick} data-testid="mortgage-payment-informer-icon">
            <Tooltip
              title={
                <MortgagePaymentTooltipContent
                  tooltipTitle={tooltipTitle}
                  tooltipLink={tooltipLink}
                  onTooltipLinkClick={onTooltipLinkClick}
                />
              }
              theme="white"
              placement="top-end"
              open={open}
              portal={false}
            >
              <IconFunctionalInformation16 color="icon-main-default" />
            </Tooltip>
          </div>
        </Outside>
      </div>

      <div className={styles['description']}>
        <UIText2 color="text-secondary-default">{programTypeWithRate}</UIText2>
      </div>
    </div>
  );
};
